import { Sector } from 'recharts';
import { PieSectorDataItem } from 'recharts/types/polar/Pie';

/**
 * creates an "active shape" for a recharts pie chart (the thing used on hover)
 */
export const createRenderActiveShape = (
    primaryTextColor: string,
    secondaryTextColor: string,
    primaryTextFn: (info: PieSectorDataItem) => string,
    secondaryTextFn?: (info: PieSectorDataItem) => string,
    fontSize: number = 14
) => (params: PieSectorDataItem) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = params;

    const primaryText = primaryTextFn(params);
    const indexOfSpace = primaryText.indexOf(' ');
    const [primaryText1, primaryText2, primaryText1Y, primaryText2Y] =
        indexOfSpace !== -1 && primaryText.length > 15
            ? [
                  primaryText.substring(0, indexOfSpace),
                  primaryText.substring(indexOfSpace + 1),
                  cy - fontSize / 2 - 2 - fontSize,
                  cy - fontSize / 2 - 2,
              ]
            : [primaryText, undefined, cy - 10, undefined];

    const secondaryText = secondaryTextFn?.(params);
    const indexOfSpaceSecondary = secondaryText?.indexOf(' ');
    const [secondaryText1, secondaryText2, secondaryText1Y, secondaryText2Y] =
        secondaryText &&
        indexOfSpaceSecondary &&
        indexOfSpace !== -1 &&
        secondaryText.length > 15
            ? [
                  secondaryText.substring(0, indexOfSpaceSecondary),
                  secondaryText.substring(indexOfSpaceSecondary + 1),
                  cy + fontSize / 2 + 2,
                  cy + fontSize / 2 + 2 + fontSize,
              ]
            : [secondaryText, undefined, cy + 10, undefined];

    return (
        <g id="active">
            <Sector
                cx={cx}
                cy={cy}
                innerRadius={innerRadius}
                outerRadius={outerRadius}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
                // todo: find a way to make this work even for small sectors
                // cornerRadius={8}
            />
            <Sector
                cx={cx}
                cy={cy}
                startAngle={startAngle}
                endAngle={endAngle}
                innerRadius={outerRadius + 6}
                outerRadius={outerRadius + 10}
                fill={fill}
                cornerRadius={3}
            />
            <LineWithDot {...params} />
            <text
                x={cx}
                y={primaryText1Y}
                textAnchor="middle"
                fill={primaryTextColor}
                fontWeight="bold"
                style={{ pointerEvents: 'none', fontSize }}
            >
                {primaryText1}
            </text>
            {primaryText2 && (
                <text
                    x={cx}
                    y={primaryText2Y}
                    textAnchor="middle"
                    fill={primaryTextColor}
                    fontWeight="bold"
                    style={{ pointerEvents: 'none', fontSize }}
                >
                    {primaryText2}
                </text>
            )}
            {secondaryText1 && (
                <text
                    x={cx}
                    y={secondaryText1Y}
                    textAnchor="middle"
                    fill={secondaryTextColor}
                    style={{ pointerEvents: 'none', fontSize }}
                >
                    {secondaryText1}
                </text>
            )}
            {secondaryText2 && (
                <text
                    x={cx}
                    y={secondaryText2Y}
                    textAnchor="middle"
                    fill={secondaryTextColor}
                    style={{ pointerEvents: 'none', fontSize }}
                >
                    {secondaryText2}
                </text>
            )}
        </g>
    );
};

function LineWithDot(props: PieSectorDataItem) {
    const { cx, cy, midAngle, innerRadius, fill } = props;

    // below, we adjust to be offset from the center a bit based on some assumed text height
    // this is frankly not great and doesn't properly account for the real text bounding box,
    // but I declare it OK for this purpose for now.
    const RADIAN = Math.PI / 180;
    const sin = Math.sin(-RADIAN * (midAngle ?? 1));
    const cos = Math.cos(-RADIAN * (midAngle ?? 1));

    const sx = cx + (innerRadius - 10) * cos;
    const sy = cy + (innerRadius - 10) * sin;

    const textClearance = 70;
    const startDistance = innerRadius - 10;
    const t = 1 - textClearance / startDistance;

    const ex = sx + t * (cx - sx);
    const ey = sy + t * (cy - sy);

    return (
        <>
            <path
                d={`M${sx},${sy}L${ex},${ey}`}
                stroke={fill}
                fill="none"
                style={{ pointerEvents: 'none' }}
            />
            <circle
                cx={ex}
                cy={ey}
                r={2}
                fill={fill}
                stroke="none"
                style={{ pointerEvents: 'none' }}
            />
        </>
    );
}
